import React, { useEffect } from 'react';
import type { OjtSession } from '../../../types';
import { PinPad, QrSignOff, SelfieCapture, subscribeToSessionFlag } from './SignOffPrimitives';
import { useTranslation } from 'react-i18next';

interface HandshakeConfig {
  pinEnabled: boolean;
  selfieEnabled: boolean;
  qrEnabled: boolean;
}

interface HandshakeProps {
  config: HandshakeConfig;
  pin: string;
  session: OjtSession;
  onComplete: () => void;
}

/**
 * Initial handshake step at the start of an OJT session — verifies the lead hand is present.
 * Delegates UI to the shared SignOffPrimitives so the look-and-feel matches mode-change
 * sign-off and other approval flows.
 */
export const Handshake: React.FC<HandshakeProps> = ({ config, pin, session, onComplete }) => {
  const { t } = useTranslation();

  // Auto-complete if no handshake method is enabled
  useEffect(() => {
    if (!config.pinEnabled && !config.selfieEnabled && !config.qrEnabled) {
      onComplete();
    }
  }, [config.pinEnabled, config.selfieEnabled, config.qrEnabled, onComplete]);

  if (config.pinEnabled) {
    return (
      <div className="p-4 max-w-md mx-auto">
        <PinPad
          expectedPin={pin}
          title={t('candidate.ojt.handshake.pin.title')}
          hint={t('candidate.ojt.handshake.pin.desc')}
          submitLabel={t('candidate.ojt.handshake.pin.verify')}
          onComplete={onComplete}
        />
      </div>
    );
  }

  if (config.selfieEnabled) {
    return (
      <div className="p-4 max-w-md mx-auto">
        <SelfieCapture
          session={session}
          storagePathSuffix="handshake_selfie.jpg"
          verifyTwoHeads
          title={t('candidate.ojt.handshake.selfie.title')}
          hint={t('candidate.ojt.handshake.selfie.desc')}
          onComplete={() => onComplete()}
        />
      </div>
    );
  }

  if (config.qrEnabled) {
    return (
      <div className="p-4 max-w-md mx-auto">
        <QrSignOff
          qrValue={`soloready-ojt-handshake:${session.id}`}
          subscribeToConfirmation={subscribeToSessionFlag(session.id, 'handshakeVerified')}
          onComplete={onComplete}
          title={t('candidate.ojt.handshake.qr.title')}
          hint={t('candidate.ojt.handshake.qr.desc')}
        />
      </div>
    );
  }

  return null;
};
